import { useCallback, useEffect, useRef, useState } from "react";
import {
  asyncTaskResult,
  asyncTasksListing,
  generateMarketingDocument,
} from "@/network/network";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { ASYNC_TASK_STATUS, ASYNC_TASK_TYPES } from "@/other/constants/OtherKeys";
import { showToast } from "@/other/utils/utils";
import { getApiErrorMessage } from "@/integrations";
import type { MarketingDocumentFormat } from "@/bryntum-lib/models/MarketingDocument";
import {
  asyncTaskIdFromResponse,
  isAcceptedAsyncGenerateResponse,
  messageFromApiPayload,
  openMarketingAsyncDialog,
} from "@/react/components/entityViews/marketing/utils/asyncGenerateHelpers";

export type DocumentGenerationPending = {
  workFileId: number;
  format: MarketingDocumentFormat;
  startedAt: number;
  knownIds: number[];
  asyncTaskId?: number;
};

const POLL_MS = 2000;

export function useMarketingDocumentGenerate() {
  const { selectedBoard, editorHandler } = useSwivuBryntumContext();
  const [submitting, setSubmitting] = useState(false);
  const [pending, setPending] = useState<DocumentGenerationPending | null>(null);
  const boardId = (selectedBoard?.record as { id?: number } | undefined)?.id;
  const pendingRef = useRef(pending);
  pendingRef.current = pending;

  const clearPending = useCallback(() => setPending(null), []);

  const startGenerateForWorkFile = useCallback(
    async (
      workFileId: number,
      format: MarketingDocumentFormat,
      name: string,
      knownDocumentIds: number[] = [],
    ) => {
      const trimmedName = name.trim();
      if (!boardId || !trimmedName) {
        showToast({
          msg: !boardId ? "No board selected." : "Document name is required.",
          type: "error",
          duration: 3000,
        });
        return false;
      }
      setSubmitting(true);
      try {
        const response = await generateMarketingDocument(boardId, {
          workFileId,
          format,
          name: trimmedName,
        });
        if (!isAcceptedAsyncGenerateResponse(response)) {
          showToast({
            msg: "Could not start document generation.",
            type: "error",
            duration: 3000,
          });
          return false;
        }
        setPending({
          workFileId,
          format,
          startedAt: Date.now(),
          knownIds: knownDocumentIds,
          asyncTaskId: asyncTaskIdFromResponse(response),
        });
        openMarketingAsyncDialog(
          editorHandler as { openEditor: (...args: unknown[]) => void },
          "MARKETING_DOCUMENT_GENERATION",
        );
        return true;
      } catch (err: unknown) {
        showToast({
          msg: getApiErrorMessage(err, "Failed to start document generation."),
          type: "error",
          duration: 4000,
        });
        return false;
      } finally {
        setSubmitting(false);
      }
    },
    [boardId, editorHandler],
  );

  useEffect(() => {
    if (!pending) return;

    let cancelled = false;

    const settleFailure = async (taskId: number, wasCancelled: boolean) => {
      let msg = wasCancelled
        ? "Document generation was cancelled."
        : "Document generation failed.";
      try {
        const result = await asyncTaskResult(taskId);
        msg = messageFromApiPayload(result?.data, msg);
      } catch {
        // Result may already be retrieved; still clear pending UI.
      }
      if (cancelled) return;
      setPending(null);
      showToast({ msg, type: "error", duration: 5000 });
    };

    const poll = async () => {
      const current = pendingRef.current;
      if (!current || cancelled) return;

      try {
        const response = await asyncTasksListing();
        if (cancelled) return;
        const tasks: any[] = response?.data ?? [];

        const task =
          (current.asyncTaskId != null
            ? tasks.find((t) => Number(t.id) === Number(current.asyncTaskId))
            : null) ??
          tasks.find(
            (t) =>
              t.type === ASYNC_TASK_TYPES.MARKETING_DOCUMENT_GENERATION &&
              Number(t.meta_data?.work_file_id) === Number(current.workFileId) &&
              (boardId == null || Number(t.meta_data?.board_id) === Number(boardId)) &&
              !t.is_retrieved,
          );

        if (!task) return;

        if (
          task.status === ASYNC_TASK_STATUS.FAILED ||
          task.status === ASYNC_TASK_STATUS.CANCELLED
        ) {
          await settleFailure(
            Number(task.id),
            task.status === ASYNC_TASK_STATUS.CANCELLED,
          );
        }
      } catch {
        // Ignore transient listing errors; next tick retries.
      }
    };

    void poll();
    const intervalId = window.setInterval(() => {
      void poll();
    }, POLL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [pending, boardId]);

  return {
    submitting,
    generating: submitting || pending != null,
    pending,
    clearPending,
    startGenerateForWorkFile,
  };
}
